import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { FiUsers, FiBox, FiPackage, FiMap, FiFileText, FiTrendingUp, FiTarget, FiSettings, FiChevronsLeft, FiChevronsRight, FiZap, FiActivity, FiMonitor, FiLogOut, FiUser } from 'react-icons/fi';
// Lucide's hand — same 24px/stroke-2/round-cap style as the fi set.
import { LuHand } from 'react-icons/lu';
import { useAuth } from '../context/AuthContext';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { username, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  const [hovered, setHovered] = useState(false);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };

  const isVisuallyCollapsed = collapsed && !hovered;

  return (
    <div
      className={`sidebar ${isVisuallyCollapsed ? 'collapsed' : ''} ${collapsed && hovered ? 'peek' : ''}`}
      onMouseEnter={() => { if (collapsed) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
    >
      <nav className="sidebar-nav">
        {/* Sales */}
        <div className="nav-group">
          {!isVisuallyCollapsed && <div className="nav-group-label">Sales</div>}
          <button
            className={`nav-item ${currentPath === '/prospecting' ? 'active' : ''}`}
            onClick={() => navigate('/prospecting')}
            title="Lead Finder"
          >
            <FiMap className="nav-icon" />
            <span className="nav-label">Lead Finder</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/contracting' ? 'active' : ''}`}
            onClick={() => navigate('/contracting')}
            title="Contracting"
          >
            <FiFileText className="nav-icon" />
            <span className="nav-label">Contracting</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/roi' ? 'active' : ''}`}
            onClick={() => navigate('/roi')}
            title="ROI Calculator"
          >
            <FiTrendingUp className="nav-icon" />
            <span className="nav-label">ROI Calculator</span>
          </button>
          <button
            className={`nav-item ${currentPath.startsWith('/power-hour') ? 'active' : ''}`}
            onClick={() => navigate('/power-hour')}
            title="Earn the Summit"
          >
            <FiZap className="nav-icon" />
            <span className="nav-label">Earn the Summit</span>
          </button>
        </div>

        {/* Customer Operations */}
        <div className="nav-group">
          {!isVisuallyCollapsed && <div className="nav-group-label">Operations</div>}
          <button
            className={`nav-item ${currentPath === '/customers' ? 'active' : ''}`}
            onClick={() => navigate('/customers')}
            title="Customers"
          >
            <FiUsers className="nav-icon" />
            <span className="nav-label">Customers</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/trials' ? 'active' : ''}`}
            onClick={() => navigate('/trials')}
            title="Trial Pipeline"
          >
            <FiTarget className="nav-icon" />
            <span className="nav-label">Trial Pipeline</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/admin' ? 'active' : ''}`}
            onClick={() => navigate('/admin')}
            title="Admin"
          >
            <FiSettings className="nav-icon" />
            <span className="nav-label">Admin</span>
          </button>
        </div>

        {/* Data Management */}
        <div className="nav-group">
          {!isVisuallyCollapsed && <div className="nav-group-label">Data Management</div>}
          <button
            className={`nav-item ${currentPath === '/e2-summit' ? 'active' : ''}`}
            onClick={() => navigate('/e2-summit')}
            title="E2 Summit (live wall)"
          >
            <FiMonitor className="nav-icon" />
            <span className="nav-label">E2 Summit</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/customer-management' ? 'active' : ''}`}
            onClick={() => navigate('/customer-management')}
            title="Customer Management"
          >
            <FiActivity className="nav-icon" />
            <span className="nav-label">Customer Management</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/data-curation' ? 'active' : ''}`}
            onClick={() => navigate('/data-curation')}
            title="Data Curation"
          >
            <LuHand className="nav-icon" />
            <span className="nav-label">Data Curation</span>
          </button>
        </div>

        {/* Logistics */}
        <div className="nav-group">
          {!isVisuallyCollapsed && <div className="nav-group-label">Logistics</div>}
          <button
            className={`nav-item ${currentPath === '/inventory' ? 'active' : ''}`}
            onClick={() => navigate('/inventory')}
            title="Inventory"
          >
            <FiBox className="nav-icon" />
            <span className="nav-label">Inventory</span>
          </button>
          <button
            className={`nav-item ${currentPath === '/shipments' ? 'active' : ''}`}
            onClick={() => navigate('/shipments')}
            title="Shipments"
          >
            <FiPackage className="nav-icon" />
            <span className="nav-label">Shipments</span>
          </button>
        </div>
      </nav>

      <div className="sidebar-footer">
        {username && !isVisuallyCollapsed && (
          <div className="sidebar-user" title={`Signed in as ${username}`}>
            <FiUser className="nav-icon" />
            <span className="nav-label">{username}</span>
          </div>
        )}
        <button
          className="nav-item sidebar-logout"
          onClick={logout}
          title="Log out"
        >
          <FiLogOut className="nav-icon" />
          <span className="nav-label">Log out</span>
        </button>
      </div>

      <button
        className="sidebar-toggle"
        onClick={toggleCollapsed}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
      </button>
    </div>
  );
};

export default Sidebar;
